/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './NumberedStatsSection.module.scss';

import { type NumberedStatsSectionProps } from './NumberedStatsSection.types';

export const NumberedStatsSection = ({
  title,
  description,
  rows,
  stats = [],
  footnote,
  background = '#F7F9FD',
  className,
}: NumberedStatsSectionProps) => {
  const rowsRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const rowsRevealed = useIntersection(rowsRef, { threshold: 0.1 });
  const statsRevealed = useIntersection(statsRef, { threshold: 0.25 });
  const [counts, setCounts] = useState(stats.map(() => 0));

  useEffect(() => {
    if (!statsRevealed) {
      return undefined;
    }

    const steps = 26;
    let step = 0;
    const id = setInterval(() => {
      step += 1;
      const progress = Math.min(step / steps, 1);
      setCounts(stats.map((stat) => Math.round(stat.value * progress)));
      if (progress >= 1) {
        clearInterval(id);
      }
    }, 28);

    return () => clearInterval(id);
  }, [statsRevealed]);

  return (
    <Section background={background} className={cn(styles.root, className)}>
      <div className={styles.head}>
        <TitleBlock title={title} description={description} />
      </div>

      <div className={cn(styles.rows, rowsRevealed && styles.rowsIn)} ref={rowsRef}>
        {rows.map((row, i) => (
          <div className={styles.row} style={{ transitionDelay: `${i * 60}ms` }} key={row.n}>
            <span className={styles.ghostN} aria-hidden="true">
              {row.n}
            </span>

            <div className={styles.titleCol}>
              <div className={styles.titleRow}>
                <span className={styles.n}>{row.n}</span>
                <h3 className={styles.title}>{row.title}</h3>
              </div>
            </div>

            <p className={styles.body}>{row.body}</p>
          </div>
        ))}
      </div>

      {stats.length > 0 && (
        <div className={cn(styles.stats, statsRevealed && styles.statsIn)} ref={statsRef}>
          {stats.map((stat, i) => (
            <div className={styles.stat} style={{ transitionDelay: `${i * 80}ms` }} key={stat.tag}>
              <span className={styles.statGlow} aria-hidden="true" />

              <span className={styles.statIcon}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {stat.icon}
                </svg>
              </span>

              <span className={styles.tag}>{stat.tag}</span>
              <span className={styles.statN}>
                {stat.prefix}
                {counts[i]}
                {stat.suffix && <em>{stat.suffix}</em>}
              </span>
              <p className={styles.statBody}>{stat.body}</p>
            </div>
          ))}
        </div>
      )}

      {footnote && <p className={styles.footnote}>{footnote}</p>}
    </Section>
  );
};
